'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SPARTAN_PRODUCT_ENGINE_MEDIA_PRELOAD_OFFSET, SPARTAN_PRODUCT_ENGINE_MEDIA_READY_TIMEOUT_MS, SPARTAN_PRODUCT_ENGINE_SWITCH_DURATION_MS } from '@/app/lcv/components/sections/spartan/products/engine/constants';
import type { SpartanProductEngineItem } from '@/app/lcv/components/sections/spartan/products/engine/types';
import { getEngineMediaKey } from '@/app/lcv/components/sections/spartan/products/engine/utils';
export type EngineSwitchPhase = 'idle' | 'out' | 'in';
interface UseEngineMediaControllerOptions {
  engines: SpartanProductEngineItem[];
  reducedMotion: boolean;
}
const clearTimer = (timerRef: {
  current: ReturnType<typeof setTimeout> | null;
}) => {
  if (timerRef.current === null) return;
  clearTimeout(timerRef.current);
  timerRef.current = null;
};
export const useEngineMediaController = t0 => {
  const $ = _c(35);
  const {
    engines,
    reducedMotion
  } = t0;
  const displayedIndexRef = useRef(0);
  const pendingIndexRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const readyIndicesRef = useRef(t1);
  const outTimerRef = useRef(null);
  const inTimerRef = useRef(null);
  const readyTimerRef = useRef(null);
  const [rootNode, setRootNode] = useState(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [hasMediaIntent, setHasMediaIntent] = useState(false);
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const [requestedIndex, setRequestedIndex] = useState(0);
  const [switchPhase, setSwitchPhase] = useState("idle");
  const [readyIndices, setReadyIndices] = useState(_temp);
  let t2;
  if ($[1] !== engines) {
    t2 = engines.map(getEngineMediaKey).join("|");
    $[1] = engines;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const mediaScopeKey = t2;
  const shouldLoadMedia = isNearViewport || hasMediaIntent;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = node => {
      setRootNode(current => current === node ? current : node);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const rootRef = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      clearTimer(outTimerRef);
      clearTimer(inTimerRef);
      clearTimer(readyTimerRef);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const clearSwitchTimers = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      clearTimer(inTimerRef);
      setSwitchPhase("in");
      inTimerRef.current = setTimeout(() => {
        setSwitchPhase("idle");
        inTimerRef.current = null;
      }, SPARTAN_PRODUCT_ENGINE_SWITCH_DURATION_MS.in);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const beginIn = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const nextIndex = pendingIndexRef.current;
      outTimerRef.current = null;
      if (nextIndex === null || nextIndex === displayedIndexRef.current) {
        pendingIndexRef.current = null;
        beginIn();
        return;
      }
      displayedIndexRef.current = nextIndex;
      pendingIndexRef.current = null;
      setDisplayedIndex(nextIndex);
      beginIn();
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const commitPending = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      clearTimer(outTimerRef);
      clearTimer(inTimerRef);
      clearTimer(readyTimerRef);
      setSwitchPhase("out");
      outTimerRef.current = setTimeout(commitPending, SPARTAN_PRODUCT_ENGINE_SWITCH_DURATION_MS.out);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const beginOut = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = index => {
      if (readyIndicesRef.current.has(index)) {
        return false;
      }
      const nextReadyIndices = new Set(readyIndicesRef.current);
      nextReadyIndices.add(index);
      readyIndicesRef.current = nextReadyIndices;
      setReadyIndices(nextReadyIndices);
      return true;
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const markEngineReady = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = index_0 => {
      if (!markEngineReady(index_0)) {
        return;
      }
      if (pendingIndexRef.current === index_0) {
        beginOut();
      }
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const handleEngineReady = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      readyTimerRef.current = null;
      pendingIndexRef.current = null;
      setRequestedIndex(displayedIndexRef.current);
      setSwitchPhase("idle");
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const cancelPendingAfterReadyTimeout = t10;
  let t11;
  if ($[11] !== engines.length || $[12] !== reducedMotion || $[13] !== switchPhase) {
    t11 = index_1 => {
      if (!Number.isInteger(index_1) || index_1 < 0 || index_1 >= engines.length) {
        return;
      }
      setHasMediaIntent(true);
      setRequestedIndex(index_1);
      if (reducedMotion) {
        clearSwitchTimers();
        pendingIndexRef.current = null;
        displayedIndexRef.current = index_1;
        setDisplayedIndex(index_1);
        setSwitchPhase("idle");
        return;
      }
      if (index_1 === displayedIndexRef.current) {
        clearSwitchTimers();
        pendingIndexRef.current = null;
        setSwitchPhase("idle");
        return;
      }
      pendingIndexRef.current = index_1;
      clearTimer(readyTimerRef);
      if (readyIndicesRef.current.has(index_1)) {
        beginOut();
        return;
      }
      clearTimer(outTimerRef);
      if (switchPhase === "out") {
        beginIn();
      }
      readyTimerRef.current = setTimeout(cancelPendingAfterReadyTimeout, SPARTAN_PRODUCT_ENGINE_MEDIA_READY_TIMEOUT_MS);
    };
    $[11] = engines.length;
    $[12] = reducedMotion;
    $[13] = switchPhase;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const handleSelectIndex = t11;
  let t12;
  let t13;
  if ($[15] !== isNearViewport || $[16] !== rootNode) {
    t12 = () => {
      if (!rootNode || isNearViewport) {
        return;
      }
      if (typeof IntersectionObserver === "undefined") {
        setIsNearViewport(true);
        return;
      }
      const observer = new IntersectionObserver(t14 => {
        const [entry] = t14;
        if (!entry.isIntersecting) {
          return;
        }
        setIsNearViewport(true);
        observer.unobserve(entry.target);
      }, {
        rootMargin: SPARTAN_PRODUCT_ENGINE_MEDIA_PRELOAD_OFFSET,
        threshold: 0
      });
      observer.observe(rootNode);
      return () => observer.disconnect();
    };
    t13 = [isNearViewport, rootNode];
    $[15] = isNearViewport;
    $[16] = rootNode;
    $[17] = t12;
    $[18] = t13;
  } else {
    t12 = $[17];
    t13 = $[18];
  }
  useEffect(t12, t13);
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      clearSwitchTimers();
      displayedIndexRef.current = 0;
      pendingIndexRef.current = null;
      readyIndicesRef.current = new Set();
      setHasMediaIntent(false);
      setDisplayedIndex(0);
      setRequestedIndex(0);
      setReadyIndices(new Set());
      setSwitchPhase("idle");
    };
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== mediaScopeKey) {
    t15 = [clearSwitchTimers, mediaScopeKey];
    $[20] = mediaScopeKey;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  useLayoutEffect(t14, t15);
  let t16;
  let t17;
  if ($[22] !== reducedMotion || $[23] !== requestedIndex) {
    t16 = () => {
      if (!reducedMotion) {
        return;
      }
      clearSwitchTimers();
      pendingIndexRef.current = null;
      displayedIndexRef.current = requestedIndex;
      setDisplayedIndex(requestedIndex);
      setSwitchPhase("idle");
    };
    t17 = [clearSwitchTimers, reducedMotion, requestedIndex];
    $[22] = reducedMotion;
    $[23] = requestedIndex;
    $[24] = t16;
    $[25] = t17;
  } else {
    t16 = $[24];
    t17 = $[25];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => clearSwitchTimers;
    t19 = [clearSwitchTimers];
    $[26] = t18;
    $[27] = t19;
  } else {
    t18 = $[26];
    t19 = $[27];
  }
  useEffect(t18, t19);
  let t20;
  if ($[28] !== displayedIndex || $[29] !== handleSelectIndex || $[30] !== readyIndices || $[31] !== requestedIndex || $[32] !== shouldLoadMedia || $[33] !== switchPhase) {
    t20 = {
      rootRef,
      displayedIndex,
      requestedIndex,
      switchPhase,
      readyIndices,
      shouldLoadMedia,
      handleEngineReady,
      handleSelectIndex
    };
    $[28] = displayedIndex;
    $[29] = handleSelectIndex;
    $[30] = readyIndices;
    $[31] = requestedIndex;
    $[32] = shouldLoadMedia;
    $[33] = switchPhase;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  return t20;
};
function _temp() {
  return new Set();
}